import { useEffect, useMemo, useState } from "react";
import { Box, Typography, Skeleton, Chip, Button } from "@mui/joy";
import { Download, ExternalLink, FileText } from "lucide-react";
import { useAsset, useDocument, useProject as useProjectQuery } from "../../../../../../hooks/query";
import { useProject, useNotification } from "../../../../../../hooks";
import { motion, AnimatePresence } from "framer-motion";

import AdditionalUploads from "../AdditionalUploads";

const MotionBox = motion.create(Box);
const EXPECTED_DOCUMENTS = [
    "CONTRACT",
    "HOUSE_OWNER_INVOICE",
    "INSURANCE_INVOICE",
    "ROOFER_INVOICE",
    "CLIENT_SIGNOFF",
    "LIEN_WAIVER",
    "SUPPLY_ORDER",
    "GUTTER_ORDER",
    "WORK_ORDER",
    "INITIAL_INSPECTION",
    "INSPECTION_PHOTO_REPORT",
    "CLAIMS_ESTIMATE",
] as const;

export default function Documentation() {
    const { project } = useProject();
    const projectId = project?.id;

    const { useList } = useDocument();
    const { useDownload } = useAsset();
    const { useGetProjectById, useGetEagleViewReport } = useProjectQuery();
    const { showError } = useNotification();

    const listSvc = useList();
    const downloadAssetSvc = useDownload();
    const projectSvc = useGetProjectById(projectId!);
    const getEvReportSvc = useGetEagleViewReport();

    const [documents, setDocuments] = useState<any[]>([]);

    const fetchDocuments = async () => {
        if (!projectId) return;
        try {
            const data = await listSvc.mutateAsync({ ProjectId: projectId });
            setDocuments(data || []);
        } catch (e) {
            console.error("Failed to fetch documents", e);
        }
    };

    const handleDownloadEagleView = async (reportId: string) => {
        try {
            const response = await getEvReportSvc.mutateAsync({ ReportId: reportId });
            const downloadUrl = response?.ReportDownloadLink;
            if (downloadUrl) {
                window.open(downloadUrl, "_blank");
            } else if (typeof response === "string" && response.startsWith("http")) {
                window.open(response, "_blank");
            } else {
                showError("EagleView report link not found.");
            }
        } catch (e: any) {
            showError(e.message || "Failed to load EagleView report. Please try again.");
        }
    };

    useEffect(() => {
        fetchDocuments();
    }, [projectId]);

    const projectDetails = projectSvc.data;

    const handleDownloadDocument = async (assetId?: string) => {
        if (!assetId) return;
        try {
            await downloadAssetSvc.mutateAsync({ AssetId: assetId });
        } catch (e: any) {
            showError(e.message || "Failed to download document.");
        }
    };

    const getDocCategory = (name: string) => {
        const n = String(name).toUpperCase();
        if (n.includes("CONTRACT") || n.includes("SIGNOFF") || n.includes("WAIVER")) return "Agreements";
        if (n.includes("INITIAL_INSPECTION") || n.includes("PHOTO_REPORT")) return "Field Reports";
        if (n.includes("WORK_ORDER") || n.includes("SUPPLY_ORDER") || n.includes("PURCHASE_ORDER") || n.includes("GUTTER")) {
            return "Ordering & Scope";
        }
        if (n.includes("EAGLEVIEW")) return "External Reports";
        if (n.includes("INVOICE") || n.includes("ESTIMATE")) return "Finance";
        return "Other";
    };

    const getDocDescription = (name: string) => {
        const n = String(name).toUpperCase();
        if (n.includes("CONTRACT")) return "Project agreement and execution copy";
        if (n.includes("SIGNOFF")) return "Customer sign-off and approval record";
        if (n.includes("WAIVER")) return "Supporting waiver and release document";
        if (n.includes("WORK_ORDER")) return "Crew-facing scope and field instructions";
        if (n.includes("SUPPLY_ORDER") || n.includes("PURCHASE_ORDER")) return "Material ordering and supplier request";
        if (n.includes("GUTTER")) return "Gutter-specific work order and scope";
        if (n.includes("INVOICE")) return "Billing and financial settlement document";
        if (n.includes("EAGLEVIEW")) return "Roof measurement and ordering reference";
        return "Project document";
    };

    const getStatusColor = (status?: string): "neutral" | "primary" | "success" | "warning" | "danger" => {
        const normalized = String(status || "").toUpperCase();
        if (normalized === "GENERATED" || normalized === "AVAILABLE" || normalized === "SIGNED") return "success";
        if (normalized === "PENDING" || normalized === "PROCESSING") return "warning";
        if (normalized === "FAILED" || normalized === "ERROR") return "danger";
        if (normalized === "SENT") return "primary";
        return "neutral";
    };

    const listedDocs: any[] = documents.map((doc: any) => ({
        title: toTitleCase(String(doc.Name)),
        name: String(doc.Name).toUpperCase(),
        description: getDocDescription(doc.Name),
        fileType: "PDF",
        status: doc.Status,
        id: doc.Id,
        assetId: doc.DocumentAssetId,
        category: getDocCategory(doc.Name),
    }));

    const listedNames = new Set(listedDocs.map((doc) => doc.name));
    const missingDocs = EXPECTED_DOCUMENTS.filter((name) => !listedNames.has(name)).map((name) => ({
        title: toTitleCase(name),
        name,
        description: getDocDescription(name),
        fileType: "PDF",
        status: "NOT_GENERATED",
        id: `missing-${name}`,
        assetId: undefined,
        category: getDocCategory(name),
        isPlaceholder: true,
    }));

    const formattedDocs: any[] = [...listedDocs, ...missingDocs];

    if (projectDetails?.EagleViewOrderId) {
        formattedDocs.unshift({
            title: "EagleView Report",
            description: `Order ID: ${projectDetails.EagleViewOrderId}`,
            fileType: "PDF",
            status: "AVAILABLE",
            id: "eagleview",
            name: "EAGLEVIEW",
            onDownload: () => handleDownloadEagleView(projectDetails.EagleViewOrderId!),
            loading: getEvReportSvc.isPending,
            category: "External Reports",
        });
    }

    const categoryMeta: Record<string, { title: string; description: string }> = {
        Agreements: {
            title: "Agreements",
            description: "Customer-facing agreements, approvals, and formal sign-off documents.",
        },
        "Field Reports": {
            title: "Field Reports",
            description: "Inspection outputs, on-site summaries, and field-generated reporting.",
        },
        "Ordering & Scope": {
            title: "Ordering & Scope",
            description: "Crew instructions, scope breakdowns, and supplier ordering paperwork.",
        },
        Finance: {
            title: "Finance",
            description: "Estimates, invoices, and project billing-related documents.",
        },
        "External Reports": {
            title: "External Reports",
            description: "Third-party measurement and reference reports tied to the project.",
        },
        Other: {
            title: "Other",
            description: "Additional project documents that do not fit the main workflow groups.",
        },
    };

    const categories = ["Agreements", "Field Reports", "Ordering & Scope", "Finance", "External Reports", "Other"];
    const isLoading = listSvc.isPending || projectSvc.isPending;
    const visibleCategories = useMemo(
        () => categories.filter((cat) => formattedDocs.some((d) => d.category === cat)),
        [formattedDocs],
    );

    if (!project) return null;

    return (
        <Box px={3} py={2} pt={1}>
            <AnimatePresence mode="wait">
                {isLoading ? (
                    <Box sx={{ borderTop: "1px solid", borderColor: "divider" }}>
                        {[...Array(6)].map((_, i) => (
                            <DocumentationSkeleton key={i} />
                        ))}
                    </Box>
                ) : (
                    <MotionBox
                        key="content"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5 }}
                        display="flex"
                        flexDirection="column"
                        gap={0}
                    >
                        <Box sx={{ mb: 2.5 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: { xs: "flex-start", sm: "center" },
                                    justifyContent: "space-between",
                                    flexDirection: { xs: "column", sm: "row" },
                                    gap: 1,
                                }}
                            >
                                <Box>
                                    <Typography level="title-lg">Documents</Typography>
                                    <Typography level="body-sm" color="neutral" sx={{ mt: 0.25 }}>
                                        Generated files, external reports, and supporting paperwork.
                                    </Typography>
                                </Box>
                                <Chip variant="soft" color="primary" size="sm">
                                    {formattedDocs.length} {formattedDocs.length === 1 ? "Document" : "Documents"}
                                </Chip>
                            </Box>
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1.25 }}>
                                {visibleCategories.map((cat) => (
                                    <Chip key={cat} variant="plain" color="neutral" size="sm">
                                        {cat}
                                    </Chip>
                                ))}
                            </Box>
                        </Box>
                        {categories.map((cat) => {
                            const filteredDocs = formattedDocs.filter((d) => d.category === cat);
                            if (filteredDocs.length === 0) return null;

                            return (
                                <Box
                                    key={cat}
                                    sx={{
                                        borderTop: "1px solid",
                                        borderColor: "divider",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            gap: 2,
                                            py: 1.5,
                                        }}
                                    >
                                        <Box>
                                            <Typography level="title-md">{categoryMeta[cat]?.title || cat}</Typography>
                                            <Typography level="body-xs" color="neutral">
                                                {categoryMeta[cat]?.description}
                                            </Typography>
                                        </Box>
                                        <Chip variant="plain" color="neutral" size="sm">
                                            {filteredDocs.length}
                                        </Chip>
                                    </Box>
                                    <Box sx={{ borderTop: "1px solid", borderColor: "divider" }}>
                                        {filteredDocs.map((doc, index) => (
                                            <DocumentRow
                                                key={doc.id || index}
                                                title={doc.title}
                                                description={doc.description}
                                                status={doc.status}
                                                loading={Boolean(doc.loading)}
                                                statusColor={getStatusColor(doc.status)}
                                                actionLabel={doc.onDownload ? "Open" : doc.assetId ? "Download" : "Not Ready"}
                                                actionIcon={doc.onDownload ? <ExternalLink size={16} /> : <Download size={16} />}
                                                actionDisabled={!doc.onDownload && !doc.assetId}
                                                onActionClick={() =>
                                                    doc.onDownload ? doc.onDownload() : handleDownloadDocument(doc.assetId)
                                                }
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            );
                        })}
                        <AdditionalUploads projectId={projectId} />
                        {formattedDocs.length === 0 && (
                            <Box
                                sx={{
                                    width: "100%",
                                    py: 8,
                                    textAlign: "center",
                                    bgcolor: "background.surface",
                                    borderRadius: 0,
                                    border: "1px dashed",
                                    borderColor: "divider",
                                }}
                            >
                                <Typography level="h4" color="neutral">
                                    No Documents Yet
                                </Typography>
                                <Typography level="body-sm" sx={{ opacity: 0.6 }}>
                                    No documentation has been uploaded for this project yet.
                                </Typography>
                            </Box>
                        )}
                    </MotionBox>
                )}
            </AnimatePresence>
        </Box>
    );
}

const DocumentationSkeleton = ({ count = 4 }: { count?: number }) => {
    return (
        <>
            {[...Array(count)].map((_, i) => (
                <Box
                    key={i}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        py: 1.75,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    <Skeleton variant="rectangular" width={40} height={40} sx={{ borderRadius: 0, flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Skeleton variant="text" width="38%" height={20} sx={{ mb: 0.5 }} />
                        <Skeleton variant="text" width="56%" height={14} />
                    </Box>
                    <Skeleton variant="rectangular" width={86} height={24} sx={{ borderRadius: "999px" }} />
                    <Skeleton variant="rectangular" width={108} height={34} sx={{ borderRadius: 0 }} />
                </Box>
            ))}
        </>
    );
};

function toTitleCase(name: string) {
    return name
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
}

function DocumentRow({
    title,
    description,
    status,
    statusColor,
    loading,
    actionLabel,
    actionIcon,
    actionDisabled,
    onActionClick,
}: {
    title: string;
    description: string;
    status?: string;
    statusColor: "neutral" | "primary" | "success" | "warning" | "danger";
    loading?: boolean;
    actionLabel: string;
    actionIcon: React.ReactNode;
    actionDisabled?: boolean;
    onActionClick: () => void;
}) {
    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "48px minmax(0, 1fr) auto auto" },
                alignItems: "center",
                gap: 1.5,
                py: 1.75,
                borderBottom: "1px solid",
                borderColor: "divider",
            }}
        >
            <Box
                sx={{
                    width: 40,
                    height: 40,
                    display: { xs: "none", md: "flex" },
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(var(--standard-palette-danger-mainChannel) / 0.08)",
                    color: "danger.solidBg",
                }}
            >
                <FileText size={20} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography level="body-md" sx={{ fontWeight: 600 }}>
                    {title}
                </Typography>
                <Typography level="body-sm" color="neutral">
                    {description}
                </Typography>
            </Box>
            <Chip
                size="sm"
                variant="soft"
                color={statusColor}
                sx={{ justifySelf: { xs: "flex-start", md: "center" } }}
            >
                {String(status || "Unknown").replace(/_/g, " ")}
            </Chip>
            <Button
                size="sm"
                variant="plain"
                color="neutral"
                loading={loading}
                endDecorator={actionIcon}
                disabled={actionDisabled}
                onClick={onActionClick}
                sx={{
                    borderRadius: 0,
                    justifySelf: { xs: "flex-start", md: "end" },
                    px: 0,
                    minHeight: 34,
                }}
            >
                {actionLabel}
            </Button>
        </Box>
    );
}
